import { has } from "lodash";

import { ColorPaletteProps } from "~/bff/components/ColorPalette";
import { ColorPaletteColors } from "~/bff/types/ColorPaletteColors";
import { FacetCountsFacetFieldsCategory } from "~/bff/types/FacetCountsFacetFieldsCategory";
import { FILTER_DIVIDER } from "~/components/products-layout/constants";
import { Nullable } from "~/types/general.types";
import { getImageSrc } from "~/utils/image/get-image-src";

import {
  AppliedFilters,
  CombinedColorFilter,
  FilterTypes,
  MultiFilter,
  ProductFilter,
  FacetsNameFromCms,
} from "./types";

export const getFacetInCms = (
  key: string,
  facetsNamesFromCms: FacetsNameFromCms[],
): FacetsNameFromCms | undefined =>
  facetsNamesFromCms?.find(
    (filter) => filter?.facet?.toLowerCase() === key?.toLowerCase(),
  );

export const getFilterType = (
  key: string,
  value: FacetCountsFacetFieldsCategory[],
): FilterTypes => {
  if (value && has(value[0], "cat_id")) {
    return FilterTypes.swatch;
  }

  if (key === "colors") {
    return FilterTypes.color;
  }

  if (key === "sku_price") {
    return FilterTypes.price;
  }

  return FilterTypes.multi;
};

export const getFilterLabel = (label: string): string => {
  return label.replace(/([A-Z])/g, " $1");
};

export const removeAllSpaces = (string: string): string => {
  return string.replace(/\s+/g, "");
};

export const getSelectedFiltersLabels = (
  key: string,
  appliedFilters?: Nullable<AppliedFilters>,
): string[] => {
  const labels: string[] = [];
  Object.entries(appliedFilters ?? {}).forEach(([filterCode, filterValue]) => {
    const [filterCategory, filterLabel] = filterCode.split(FILTER_DIVIDER);
    const trimmedFilterCategory = removeAllSpaces(filterCategory);
    const trimmedKey = removeAllSpaces(key);
    if (trimmedFilterCategory === trimmedKey && filterValue !== false) {
      labels.push(filterLabel);
    }
  });

  return labels;
};

const getCombinedColorFilter = ({
  colorPaletteItem,
  colorFiltersItem,
}: {
  colorPaletteItem: ColorPaletteColors;
  colorFiltersItem: MultiFilter;
}) => ({
  ...colorPaletteItem,
  count: colorFiltersItem.count,
  colorName:
    colorFiltersItem.name ?? colorPaletteItem.colorName?.toLocaleLowerCase(),
  colorImage: {
    ...colorPaletteItem,
    src: getImageSrc(colorPaletteItem?.colorImage?.source),
  },
});

export const getCombinedColorFilters = (
  colorPalette: ColorPaletteProps,
  colorFilters: MultiFilter[],
): CombinedColorFilter[] => {
  const colorPaletteByColorName: {
    [key: string]: Nullable<ColorPaletteColors> | undefined;
  } = {};
  if (colorPalette && colorPalette.colors) {
    for (const colorPaletteItem of colorPalette.colors) {
      if (colorPaletteItem?.colorName) {
        colorPaletteByColorName[colorPaletteItem?.colorName?.toLowerCase()] =
          colorPaletteItem;
      }
    }
  }

  const combinedColorFilters: CombinedColorFilter[] = [];
  for (const colorFiltersItem of colorFilters) {
    if (colorFiltersItem?.name) {
      const colorPaletteItem =
        colorPaletteByColorName[colorFiltersItem.name.toLowerCase()];
      if (colorPaletteItem) {
        combinedColorFilters.push(
          getCombinedColorFilter({ colorPaletteItem, colorFiltersItem }),
        );
      }
    }
  }

  return combinedColorFilters;
};

export const isCountValueEmpty = (values: ProductFilter[]): boolean => {
  if (!values) {
    return true;
  }

  return values?.every((value) => value?.count === 0);
};
